import {
  PickupLocationStylesDescriptor,
  PickupTimeStylesDescriptor,
  ProductDetailsStylesDescriptor,
  MenuListStylesDescriptor,
  FlashListPropsType,
  Identifiable,
  ProductComponentProps,
  AddItemButtonProps,
  UpsellButtonProps,
  DepositStylesDescriptor,
} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import Styles from 'src/components/Styles';
import {
  StyleSheet,
  View,
  Image,
  PixelRatio,
  Dimensions,
  Text,
  FlatList,
  Platform,
} from 'react-native';
import Localized from 'src/constants/AppStrings';
import {ensureHttps} from 'src/services/StringUtils';
import AVText from 'src/components/elements/AVText';
import MenuItemList from 'src/components/elements/orderAhead/MenuListItem';
import {MenuProductType} from 'src/types/Menu';
import {DNA} from 'src/types/dnaTypes';
import AccountStore from 'src/stores/AccountStore';
import Util from 'src/Util';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import QuickApplyIcon from 'src/components/img/svg/QuickApplyIcon';
import {TransactionDetail} from '../../../../types/TransactionDetail';
import TransactionStore from '../../../../stores/TransactionStore';
import {ListScanItemsProps} from 'src/components/screens/scanAndPay/listScanItems/ListScanItems';
import RecentComboIcon from 'src/components/img/svg/RecentComboIcon';
import BulletPoint from 'src/components/img/svg/BulletPoint';
import {
  DietaryHealthGood,
  DietaryHealthWarning,
  DietaryHealthAlert,
} from 'src/components/img/svg/DietaryHealthGrades';
import AVFormattedCurrency from 'src/components/elements/AVFormattedCurrency';
import TagLightIcon from 'src/components/img/svg/TagLightIcon';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

const {shadowStyles, bodyFontSize, headerFontSize, subHeaderFontSize, mainFontFamily, bodySmallFontSize} =
  getLayoutDescriber();

export function getPickupLocationStyles(): PickupLocationStylesDescriptor {
  return StyleSheet.create({
    buttonContainerStyle: {
      position: 'absolute',
      alignSelf: 'flex-end',
      bottom: Styles.Spacing.m3 + Styles.Spacing.m2,
      paddingHorizontal: Styles.Spacing.m3,
    },
    buttonText: {
      fontFamily: mainFontFamily(),
      fontSize: bodyFontSize(),
      fontWeight: '700',
    },
    headerText: {
      fontWeight: '700',
      fontSize: subHeaderFontSize(),
      color: Styles.darkColor,
      fontFamily: mainFontFamily(),
    },
    errorText: {
      fontWeight: '700',
      fontSize: bodyFontSize(),
      color: Styles.Colors.PayNew.error,
      paddingHorizontal: Styles.Spacing.m1,
      alignItems: 'center',
      fontFamily: mainFontFamily(),
    },
    errorContainer: {
      flexDirection: 'row',
      paddingTop: Styles.Spacing.m2,
      alignItems: 'center',
    },
    upsellPointsView: {
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      paddingTop: 5,
    },
    upsellPointsText: {
      flex: 6.5,
      paddingRight: Styles.Spacing.m2,
      fontSize: bodySmallFontSize(),
      fontWeight: '400',
      color: Styles.Colors.PayNew.darkHuesBase04,
      fontFamily: mainFontFamily(),
    },
  });
}

export function renderNutritionPromotionComponent(
  nutritionPromoName: string,
  nutritionDiscountAmount: number,
) {
  return (
    !isNaN(nutritionDiscountAmount) &&
    nutritionPromoName !== undefined &&
    nutritionPromoName !== '' && (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          marginTop: -Styles.Spacing.m2,
          paddingBottom: Styles.Spacing.m2,
        }}
      >
        <View
          testID={'qa_NutritionPromoLabel_txt'}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            width: '62%',
          }}
        >
          <TagLightIcon
            fill={Styles.Colors.PayNew.primary01}
            style={{marginRight: Styles.Spacing.m2}}
          />
          <AVText
            testID={'qa_NutritionPromoLabel_txt'}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            style={{
              color: Styles.Colors.PayNew.primary01,
              fontFamily: mainFontFamily(),
              fontSize: bodyFontSize(),
            }}
          >
            {nutritionPromoName}
          </AVText>
        </View>

        <View
          testID={'qa_NutritionPromoValue_txt'}
          style={{
            width: '38%',
            alignItems: 'flex-end',
          }}
        >
          <AVFormattedCurrency
            currency={AccountStore.getCurrency()}
            value={nutritionDiscountAmount * -1}
            style={{
              color: Styles.Colors.PayNew.primary01,
              fontFamily: mainFontFamily(),
              fontSize: bodyFontSize(),
            }}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessibilityLabel={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}`}
            accessibilityRole="text"
            aria-label={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}, text`}
          />
        </View>
      </View>
    )
  );
}

export function renderColorNutritionComponent(
  nutritionDisplayColor: string,
  nutritionDisplayLetter: string,
  nutritionCalorieTotal: number,
) {
  let healthGradeIcon: JSX.Element | false;
  if (
    nutritionDisplayLetter?.toUpperCase() === 'G' &&
    nutritionDisplayColor?.toUpperCase() === 'GREEN'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: good, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: good, "
      >
        <DietaryHealthGood />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'Y' &&
    nutritionDisplayColor?.toUpperCase() === 'YELLOW'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: warning, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: warning, "
      >
        <DietaryHealthWarning />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'R' &&
    nutritionDisplayColor?.toUpperCase() === 'RED'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: alert, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: alert, "
      >
        <DietaryHealthAlert />
      </View>
    );
  }

  return (
    <View>
      {nutritionCalorieTotal > 0 && (
        <View>
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              justifyContent: 'flex-start',
              marginLeft: Styles.Spacing.m1,
              marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
              marginBottom: -2,
            }}
          >
            <View
              style={{paddingHorizontal: Styles.Spacing.m2 + 2, paddingTop: 4}}
            >
              {healthGradeIcon}
            </View>
            <View
              style={{
                alignItems: 'flex-start',
              }}
            >
              <AVText
                testID={'qa_CaloriesValue_txt'}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                style={{
                  color: '#22212D',
                  fontSize: subHeaderFontSize(),
                  fontFamily: mainFontFamily(),
                  fontWeight: 'bold',
                  alignItems: 'flex-start',
                  paddingLeft: Styles.Spacing.m1,
                }}
              >
                {nutritionCalorieTotal}
              </AVText>
            </View>
            <View style={Styles.Style.labelColumn2}>
              <AVText
                testID={'qa_TotalCalories_txt'}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                style={{
                  color: '#22212D',
                  fontSize: subHeaderFontSize(),
                  fontFamily: mainFontFamily(),
                  fontWeight: 'bold',
                  alignItems: 'flex-start',
                  paddingLeft: Styles.Spacing.m1,
                }}
              >
                {this.props?.location?.locationFeatures?.dna
                  ?.showCaloriesAsKilocalories
                  ? Localized.Labels.total_kilocalories
                  : Localized.Labels.total_calories}
              </AVText>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

export function getInactiveButtonColor() {
  return Styles.Colors.PayNew.darkHuesBase04;
}

export function textColor() {
  return Styles.Colors.PayNew.black01;
}
export function getActiveButtonColor() {
  return Styles.Colors.Refive.activeFooterBlue;
}

export function contactPermissionErrorText() {
  return Localized.Labels.formatString(
    Localized.Labels.choose_contact_permission_error,
    Localized.Labels.app_name_refive,
  );
}

export function getPickupTimeStyles(): PickupTimeStylesDescriptor {
  return StyleSheet.create({
    dateView: {
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
      marginLeft: Styles.Spacing.m3,
    },
    timeView: {
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
    },
    pickupTimeContainer: {
      flex: 1,
      backgroundColor: Styles.tabBarBackgroundColor,
      marginTop: Styles.Heights.h2,
    },
    pickupLocationHeaderView: {
      flexDirection: 'row',
      width: '100%',
      minHeight: Styles.Heights.touchTargetHeight2,
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingRight: Styles.Heights.h1,
      marginLeft: Styles.Spacing.m3,
    },
    customTileButtonTime: {
      minWidth: 93,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: Styles.Spacing.m1,
    },
    tilesStyle: {
      marginRight: Styles.Spacing.m3 - 4,
      marginTop: Styles.Spacing.m3,
    },
    errorContainer: {
      flexDirection: 'row',
      marginTop: Styles.Spacing.m3 + 4,
      marginBottom: Styles.Spacing.m2,
      alignItems: 'center',
    },
    buttonTextStyle: {
      fontWeight: '700',
      fontSize: bodySmallFontSize(),
      paddingHorizontal: Styles.Spacing.m1,
      marginBottom: 1,
      fontFamily: mainFontFamily(),
      color: Styles.Colors.Refive.activeFooterBlue,
    },
    editButton: {
      flexDirection: 'row',
      alignItems: 'center',
      borderColor: Styles.Colors.Refive.activeFooterBlue,
      borderRadius: 32,
      borderWidth: 1,
      paddingVertical: Styles.Spacing.m2,
      paddingHorizontal: Styles.Spacing.m2 + 2,
      marginRight: Styles.Spacing.m3,
      overflow: 'hidden',
    },
    dateText: {
      fontWeight: '700',
      fontSize: bodyFontSize(),
      color: Styles.darkColor,
      fontFamily: mainFontFamily(),
      textTransform: 'capitalize',
    },
  });
}

export function locationErrorMessage() {
  return Localized.Errors.please_select_location;
}
export function dayErrorMessage() {
  return Localized.Errors.please_select_day;
}
export function timeErrorMessage() {
  return Localized.Errors.please_select_time;
}

export function productDetailsTitle() {
  return Localized.Labels.menu_item;
}

export function productDetailsImageView(imageUri: string | null): JSX.Element {
  return (
    <>
      <View>
        {imageUri ? (
          <Image
            accessible={true}
            accessibilityLabel="Product image"
            accessibilityRole="image"
            aria-label="Product image"
            role="img"
            style={{aspectRatio: 16 / 9}}
            source={{uri: ensureHttps(imageUri)}}
            resizeMode={'cover'}
          />
        ) : (
          <Image
            accessible={true}
            accessibilityLabel="Default Product image"
            accessibilityRole="image"
            aria-label="Default Product image"
            role="img"
            source={require('src/components/img/DiningMenuItem.png')}
            resizeMode={'cover'}
            style={{width: '100%'}}
          />
        )}
      </View>
    </>
  );
}

export function productDetailsStyles(): ProductDetailsStylesDescriptor {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: Styles.tabBarBackgroundColor,
    },
    content: {
      marginVertical: Styles.Spacing.m1,
    },
    title: {
      fontWeight: '700',
      fontSize: headerFontSize(),
      color: Styles.Colors.PayNew.black01,
      fontFamily: mainFontFamily(),
      marginBottom: Styles.Spacing.m2,
    },
    infoContainer: {
      marginHorizontal: Styles.Spacing.m3,
      paddingVertical: Styles.Spacing.m1,
      flexDirection: 'column',
    },
    productPrice: {
      fontWeight: '700',
      marginTop: 1,
      fontSize: subHeaderFontSize(),
      fontFamily: mainFontFamily(),
      color: Styles.Colors.PayNew.black01,
    },
    calories: {
      fontWeight: '700',
      color: Styles.Colors.PayNew.black01,
      fontSize: subHeaderFontSize(),
      fontFamily: mainFontFamily(),
    },
    calorieText: {
      fontSize: subHeaderFontSize(),
      fontWeight: '700',
      color: Styles.Colors.PayNew.black01,
      marginLeft: 5,
      fontFamily: mainFontFamily(),
    },
    healthGrade: {
      marginLeft: Styles.Spacing.m2,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: Styles.Spacing.m1 + 2,
    },
    verticalLine: {
      marginHorizontal: Styles.Spacing.m2,
      backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
      borderColor: Styles.Colors.PayNew.darkHuesBase04,
      borderWidth: 0.8,
    },
    nutritionButtonView: {
      flexDirection: 'row',
      minHeight: 44,
    },
    outlined: {
      borderRadius: 32,
      borderColor: Styles.Colors.Refive.activeFooterBlue,
      paddingVertical: Styles.Spacing.m2,
      paddingHorizontal: Styles.Spacing.m3,
      borderWidth: 2,
      marginTop: Styles.Spacing.m2,
      overflow: 'hidden',
      backgroundColor: Styles.white,
    },
    outlinedButtonText: {
      color: Styles.Colors.Refive.activeFooterBlue,
      fontSize: bodyFontSize(),
      fontWeight: '700',
      fontFamily: mainFontFamily(),
    },
    descriptionContainer: {
      marginHorizontal: Styles.Spacing.m3,
    },
    description: {
      fontSize: bodySmallFontSize(),
      color: Styles.Colors.PayNew.darkHuesBase04,
      marginTop: Styles.Spacing.m2,
      fontFamily: mainFontFamily(),
    },
    customizationSection: {
      flex: 1,
      flexDirection: 'column',
    },
    quantityContainer: {
      marginTop: Styles.Spacing.m0,
    },
    quantitySelectionView: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    dashStyle: {
      borderRadius: 100,
      height: PixelRatio.roundToNearestPixel(2),
      overflow: 'hidden',
      width: '100%',
    },
    quantityLabel: {
      fontSize: subHeaderFontSize(),
      color: Styles.Colors.PayNew.black01,
      fontWeight: '400',
      fontFamily: mainFontFamily(),
      marginRight: Styles.Spacing.m3 + Styles.Spacing.m2,
      minHeight: 48,
      marginTop: 10,
    },
  });
}

export function roundedButtonColor() {
  return Styles.Colors.Refive.activeFooterBlue;
}
export function dashLineColor() {
  return Styles.Colors.PayNew.neutralHuesBase09;
}

export function menuListStyles(): MenuListStylesDescriptor {
  return StyleSheet.create({
    content: {
      flex: 1,
      backgroundColor: Styles.tabBarBackgroundColor,
      marginBottom: -35,
    },
    searchContainer: {
      marginLeft: Styles.Spacing.m3,
      marginRight: Styles.Spacing.m3,
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: Styles.Spacing.m3,
      marginTop: Styles.Spacing.m3,
    },
    categories: {
      marginTop: Styles.Spacing.m1,
      paddingBottom: Styles.Spacing.m2 + 2,
    },
  });
}

export function getFlashListProps<T extends Identifiable>({
  data,
  renderItem,
  refreshKey,
  isSubMenu,
}: FlashListPropsType<T>) {
  return {
    data,
    keyExtractor: (item: T) => item?.id.toString(),
    renderItem,
    ListEmptyComponent: (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          marginHorizontal: Styles.Spacing.m3,
          marginTop: Dimensions.get('screen').width / 3,
          gap: 24,
        }}
      >
        <AVText
          style={{
            fontSize: bodyFontSize(),
            fontFamily: mainFontFamily(),
            fontWeight: '700',
            color: Styles.Colors.PayNew.black01,
          }}
        >
          {Localized.Labels.no_pdt_found}
        </AVText>
        <View style={{alignSelf: 'center'}}>
          <RecentComboIcon />
        </View>
      </View>
    ),
    estimatedItemSize: 72,
    numColumns: getNumColumns(isSubMenu),
    contentContainerStyle: {
      paddingLeft: Styles.Spacing.m3,
      paddingTop: Styles.Spacing.m2,
    },
    extraData: refreshKey,
    ListFooterComponent: <View style={{height: Styles.Heights.h4}} />,
  };
}

const getNumColumns = (isSubMenu): number => {
  if (Platform.OS == 'web') {
    if (isSubMenu) {
      return 1;
    } else {
      return 3;
    }
  } else if (isSubMenu) {
    return 1;
  } else {
    return 2;
  }
};

export function getProductComponent(props: ProductComponentProps) {
  return (
    <MenuItemList
      dnaConfiguration={props?.dnaConfiguration}
      onPress={props?.onPress}
      name={props?.name}
      item={props?.item}
      price={props?.price}
      imageUrl={props?.imageUrl}
      isProductCat={props?.isProductCat}
      index={props?.index}
      updateLineCounts={props?.updateLineCounts}
      extraPadding={props?.extraPadding}
      overrideDna={props.overrideDna}
      key={props?.index}
    />
  );
}

export function getTransactionUpsellName(name: string) {
  return (
    <Text
      style={{
        fontFamily: mainFontFamily(),
        fontSize: bodyFontSize(),
        color: Styles.Colors.PayNew.darkHuesBase04,
        fontWeight: '400',
      }}
      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
      accessible
      testID="qa_Checkout.UpsellTitle_txt"
      nativeID="qa_Checkout.UpsellTitle_txt"
    >
      {name}
    </Text>
  );
}

export function getTransactionUpsellProductsList(
  products: MenuProductType[],
  addItem: (item: MenuProductType) => void,
  dna?: DNA,
  props?: ListScanItemsProps,
) {
  const calorieUnit = dna?.showCaloriesAsKilocalories
    ? Localized.Labels.kcal
    : Localized.Labels.cal_not_all_caps;

  return (
    <View style={{marginHorizontal: -Styles.Spacing.m3}}>
      <FlatList
        horizontal
        data={products}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={() => <View style={{width: 16}} />}
        ListFooterComponent={() => <View style={{width: 16}} />}
        ItemSeparatorComponent={() => <View style={{width: 12}} />}
        renderItem={({item}) => {
          const caloriesValue = item?.calories;
          const price = Util.formatCurrency(
            props,
            item.price,
            AccountStore.getCurrency(),
          );
          return (
            <View
              style={{
                flex: 1,
                padding: 8,
                justifyContent: 'space-between',
                borderRadius: Styles.Spacing.m2,
                marginBottom: Styles.Spacing.m2,
                marginTop: Styles.Spacing.m3,
                width: 166,
                ...shadowStyles().lightShadow,
              }}
            >
              <View>
                {item.smallimageurl ? (
                  <Image
                    accessible
                    aria-label={`${item.name} Image`}
                    accessibilityLabel={`${item.name} Image`}
                    role="img"
                    accessibilityRole="image"
                    source={{uri: ensureHttps(item.smallimageurl)}}
                    style={{
                      width: 148,
                      height: 125,
                      backgroundColor: Styles.Colors.PayNew.white01,
                      borderRadius: Styles.Spacing.m2,
                    }}
                    resizeMode="cover"
                  />
                ) : (
                  <Image
                    accessible
                    accessibilityLabel={'Default-Product Image'}
                    accessibilityRole="image"
                    aria-label="Default-Product Image"
                    role="img"
                    style={{
                      width: 148,
                      height: 125,
                      backgroundColor: Styles.Colors.PayNew.white01,
                      borderWidth: 1,
                      borderColor: Styles.Colors.PayNew.neutralHuesBase09,
                      borderRadius: Styles.Spacing.m2,
                    }}
                    source={require('src/components/img/DefaultMenuItem.png')}
                    resizeMode="cover"
                  />
                )}
                <View
                  style={{
                    marginHorizontal: Styles.Spacing.m1,
                    marginTop: Styles.Spacing.m1,
                  }}
                >
                  <Text
                    style={{
                      fontFamily: mainFontFamily(),
                      fontSize: bodyFontSize(),
                      color: Styles.Colors.PayNew.black01,
                      fontWeight: '700',
                      width: 144,
                    }}
                    numberOfLines={0}
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                  >
                    {item.name}
                  </Text>
                  {item?.RewardPoints > 0 && (
                    <Text
                      style={{
                        fontSize: bodySmallFontSize(),
                        fontWeight: '400',
                        color: Styles.Colors.PayNew.darkHuesBase04,
                        fontFamily: mainFontFamily(),
                        width: 144,
                      }}
                      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                    >
                      {item.RewardPoints}{' '}
                      {Localized.Labels.points.toLowerCase()}
                    </Text>
                  )}
                </View>
              </View>

              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginHorizontal: Styles.Spacing.m1,
                    marginTop: Styles.Spacing.m1,
                  }}
                >
                  <Text
                    accessible
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                    style={{
                      fontFamily: mainFontFamily(),
                      fontSize: bodyFontSize(),
                      color: Styles.Colors.PayNew.black01,
                      fontWeight: '400',
                      width: 144,
                      lineHeight: 23,
                    }}
                    accessibilityLabel={`${caloriesValue} Text`}
                    accessibilityRole="text"
                    aria-label={`${caloriesValue} Text`}
                  >
                    {`${price} `}
                    {dna?.showCalories && caloriesValue !== null && (
                      <View
                        style={{
                          height: 20,
                          justifyContent: 'center',
                          alignItems: 'center',
                          paddingTop: 3,
                        }}
                      >
                        <BulletPoint />
                      </View>
                    )}
                    {dna?.showCalories &&
                      caloriesValue !== null &&
                      ` ${caloriesValue} ${calorieUnit}`}
                  </Text>
                </View>

                <View
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: Styles.Spacing.m2,
                  }}
                >
                  <View>
                    <RoundedAppButton
                      buttonViewStyle={{
                        height: 32,
                        paddingLeft: 12,
                        paddingRight: 12,
                      }}
                      titleText={Localized.Buttons.quick_apply}
                      buttonType={RoundedAppButtonType.Outline}
                      buttonIcon={<QuickApplyIcon />}
                      textStyle={{
                        fontSize: bodySmallFontSize(),
                        fontFamily: mainFontFamily(),
                        fontWeight: '700',
                        marginTop: -2,
                      }}
                      onPress={() => addItem(item)}
                      iconStyle={{marginRight: 6}}
                      accessibilityLabelValue={Localized.Buttons.quick_apply}
                      aria-label={Localized.Buttons.quick_apply}
                      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                    />
                  </View>
                </View>
              </View>
            </View>
          );
        }}
      />
    </View>
  );
}

export function renderTenderDiscount(
  transaction: TransactionDetail | null,
  props: ListScanItemsProps,
) {
  let onScreenPromotionDiscount = 0;
  const discount = parseFloat(transaction?.PromotionDiscount ?? '');
  const afterTaxDiscount = parseFloat(transaction?.AfterTaxDiscount ?? '');
  if (transaction?.hasOwnProperty('OnScreenDiscount')) {
    onScreenPromotionDiscount = transaction?.OnScreenDiscount ?? 0;
  }
  let totalTenderDiscount = discount - onScreenPromotionDiscount;
  totalTenderDiscount = Math.round(totalTenderDiscount * 100) / 100;
  if (TransactionStore.isAfterTaxPromoApplied()) {
    totalTenderDiscount = totalTenderDiscount - afterTaxDiscount;
  }
  totalTenderDiscount = Math.round(totalTenderDiscount * 100) / 100;
  if (totalTenderDiscount && totalTenderDiscount > 0) {
    return (
      <View style={{marginVertical: 18, marginHorizontal: Styles.Spacing.m3}}>
        <Text
          style={{
            fontSize: bodyFontSize(),
            fontFamily: mainFontFamily(),
            fontStyle: 'italic',
            fontWeight: '400',
            color: Styles.Colors.PayNew.black01,
          }}
        >
          {Localized.Labels.formatString(
            Localized.Labels.you_are_saving_amount_by_using_the_app,
            Util.formatCurrency(
              props,
              totalTenderDiscount,
              AccountStore.getCurrency(),
            ),
          )}
        </Text>
      </View>
    );
  }
  return null;
}

export function getEditLocationIconColor() {
  return Styles.Colors.Refive.activeFooterBlue;
}

export function activeCalendarIconColor() {
  return Styles.Colors.Refive.navy;
}

export function inActiveCalendarIconColor() {
  return Styles.blackBase01;
}

export function calorieTextColor() {
  return Styles.Colors.PayNew.black01;
}

export function getEmptyCartlblStyle() {
  return {
    fontFamily: mainFontFamily(),
    fontSize: bodyFontSize(),
    fontWeight: '400',
    textAlign: 'left',
    color: '#22212D',
  };
}

export function getAndroidImageHeight() {
  return Styles.ScreenHeight < 700 ? 435 : 330;
}

export function getDashedLines() {
  return null;
}

export function showTotalAmount() {
  return null;
}

export function getAddItemButton(props: AddItemButtonProps) {
  return (
    <RoundedAppButton
      isDisabled={props.isDiabled}
      accessibilityLabelValue="Add item"
      aria-label="Add item"
      aria-disabled={props.actionButtonPressed}
      buttonType={RoundedAppButtonType.Solid}
      titleText={props.label}
      onPress={props.onPress}
      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
      buttonContainerStyle={styles.actionButtonStyle}
      buttonViewStyle={{
        paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
        backgroundColor: roundedButtonColor(),
      }}
      textStyle={{marginBottom: 1}}
      inActiveButtonViewStyle={{
        backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
        paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
      }}
      testID="qa_AddItem_btn"
    />
  );
}

export function upsellSelectedBackground() {
  return 'rgba(0, 109, 255, 0.15)';
}
export function upsellSheetStyles() {
  return StyleSheet.create({
    upsellSelectedView: {
      borderWidth: 1.5,
      borderColor: Styles.Colors.PayNew.primary01,
    },
  });
}

export function getUpsellButton(props: UpsellButtonProps) {
  return (
    <View
      style={{
        height: 96,
        width: '100%',
        paddingTop: 16,
        alignItems: 'center',
      }}
    >
      <RoundedAppButton
        buttonViewStyle={{
          height: 48,
          width: Dimensions.get('screen').width - 48,
        }}
        buttonType={RoundedAppButtonType.Solid}
        titleText={props.label}
        onPress={props.onPress}
        isDisabled={props.isDiabled}
        buttonContainerStyle={{
          alignSelf: 'center',
          width: Dimensions.get('screen').width - 48,
        }}
        textStyle={{
          fontSize: bodyFontSize(),
          fontWeight: '700',
        }}
        inActiveButtonViewStyle={{
          backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
          height: 48,
          width: Dimensions.get('screen').width - 48,
        }}
      />
    </View>
  );
}

export function getiOSImageHeight() {
  return Styles.ScreenHeight < 700 ? 400 : 440;
}

const styles = StyleSheet.create({
  actionButtonStyle: {
    right: Styles.Spacing.m3,
    position: 'absolute',
    bottom: Styles.Spacing.m3 + 6,
  },
});

export function depositStyles(): DepositStylesDescriptor {
  return StyleSheet.create({
    depositView: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    transactionItemsDeposit: {
      color: Styles.Colors.PayNew.black01,
      fontFamily: mainFontFamily(),
      fontSize: bodyFontSize(),
    },
  });
}

const announcementPopUpButtonColor = '#3070ED';

export default {
  getPickupLocationStyles,
  getEmptyCartlblStyle,
  getInactiveButtonColor,
  textColor,
  getPickupTimeStyles,
  getActiveButtonColor,
  locationErrorMessage,
  dayErrorMessage,
  timeErrorMessage,
  productDetailsTitle,
  productDetailsImageView,
  productDetailsStyles,
  roundedButtonColor,
  dashLineColor,
  menuListStyles,
  getFlashListProps,
  getProductComponent,
  getTransactionUpsellName,
  getTransactionUpsellProductsList,
  getEditLocationIconColor,
  activeCalendarIconColor,
  inActiveCalendarIconColor,
  renderTenderDiscount,
  renderColorNutritionComponent,
  renderNutritionPromotionComponent,
  getAndroidImageHeight,
  getDashedLines,
  calorieTextColor,
  showTotalAmount,
  contactPermissionErrorText,
  getAddItemButton,
  upsellSelectedBackground,
  upsellSheetStyles,
  getUpsellButton,
  getiOSImageHeight,
  announcementPopUpButtonColor,
  depositStyles,
};
